'use client';

import styled from 'styled-components';

import type { AriaRole, CSSProperties, KeyboardEventHandler, MouseEventHandler } from 'react';
import { starfishCssUrl } from './starfish';

import type { StarfishCategory, StarfishIconsByCategory } from './starfishManifest';

type Props = {
  /** A number or a unitless string is pixels, as on the library's `<svg>`. With no size the icon follows the font size. */
  size?: number | string;
  width?: number | string;
  height?: number | string;
  color?: string;
  title?: string;
  className?: string;
  style?: CSSProperties;
  /** Typed on `Element` so an SVG handler still fits: the library's `IconType` slots pass those. */
  onClick?: MouseEventHandler<Element>;
  onKeyPress?: KeyboardEventHandler<Element>;
  /** A few call sites make the icon itself the control. */
  role?: AriaRole;
  tabIndex?: number;
  'aria-label'?: string;
  'data-testid'?: string;
};

const cssLength = (value: number | string): string =>
  typeof value === 'number' || /^\d+(\.\d+)?$/.test(value) ? `${value}px` : value;

/**
 * `contain` rather than `100% 100%`: a library icon letterboxed its artwork into whatever box it was
 * given, and several call sites give it a box that is not square.
 */
const Glyph = styled.i<{ $width: string; $height: string; $inkScale: number }>`
  display: inline-block;
  width: ${({ $width }) => $width};
  height: ${({ $height }) => $height};
  background-color: currentColor;
  mask-image: var(--starfish-mask);
  mask-repeat: no-repeat;
  mask-position: center;
  mask-size: contain;
  -webkit-mask-image: var(--starfish-mask);
  -webkit-mask-repeat: no-repeat;
  -webkit-mask-position: center;
  -webkit-mask-size: contain;
  ${({ $inkScale }) => ($inkScale === 1 ? '' : `scale: ${$inkScale};`)}
`;

/**
 * A Starfish glyph in the shape of a `@lmn/lastminute-react-icons` component, so a call site keeps
 * the props, the CSS and the design system slots (`icon`, `iconLeading`) it drew the library icon
 * with. The box is exactly the one the library's `<svg>` had: `size`, else `width`/`height`, else
 * `1em`, and any CSS the call site sets on it still wins. The colour is the text colour, as it was.
 *
 * `inkScale` is the library glyph's ink over the Starfish glyph's, measured on the longer side of
 * each drawing. Only the glyph is scaled, through `scale` so it composes with the rotations some
 * call sites put on `transform`; the layout never sees it. It cannot overflow the box, because the
 * scaled Starfish ink is exactly as big as the library's, which fitted.
 */
const starfishLibraryIcon = <C extends StarfishCategory>(
  category: C,
  name: StarfishIconsByCategory[C],
  inkScale = 1
) => {
  const mask = `url('${starfishCssUrl(category, name)}')`;

  const StarfishLibraryIcon = ({
    size,
    width,
    height,
    color,
    title,
    className,
    style,
    onClick,
    onKeyPress,
    role,
    tabIndex,
    'aria-label': ariaLabel,
    'data-testid': testId,
  }: Props) => (
    <Glyph
      {...(title || ariaLabel || role
        ? { role: role ?? 'img', 'aria-label': ariaLabel ?? title, title }
        : { 'aria-hidden': true })}
      tabIndex={tabIndex}
      data-library-icon={`${category}/${name}`}
      data-testid={testId}
      className={className}
      onClick={onClick}
      onKeyPress={onKeyPress}
      $width={cssLength(size ?? width ?? '1em')}
      $height={cssLength(size ?? height ?? width ?? '1em')}
      $inkScale={inkScale}
      style={{ '--starfish-mask': mask, color, ...style } as CSSProperties}
    />
  );
  StarfishLibraryIcon.displayName = `StarfishLibraryIcon(${category}/${name})`;

  return StarfishLibraryIcon;
};

// One per library glyph the app drew. A linear and a solid cut share an export when they map to the
// same Starfish glyph at the same ink; otherwise each keeps its own scale.
export const IconArrowDownLinear = starfishLibraryIcon('Arrows', 'icon-chevron-down', 1.12);
export const IconArrowDownSolid = starfishLibraryIcon('Arrows', 'icon-chevron-down', 1.18);
export const IconArrowLeftLinear = starfishLibraryIcon('Arrows', 'icon-chevron-left', 1.12);
export const IconArrowLeftSolid = starfishLibraryIcon('Arrows', 'icon-chevron-left', 1.18);
export const IconArrowRightLinear = starfishLibraryIcon('Arrows', 'icon-chevron-right', 1.12);
export const IconArrowRightSolid = starfishLibraryIcon('Arrows', 'icon-chevron-right', 1.18);
export const IconArrowUp = starfishLibraryIcon('Arrows', 'icon-chevron-up', 1.12);
export const IconChevronRight = starfishLibraryIcon('Arrows', 'icon-small-chevron-right', 1.09);

export const IconCheckLinear = starfishLibraryIcon('Semantics', 'icon-check', 1.12);
export const IconCheckSolid = starfishLibraryIcon('Semantics', 'icon-check', 1.18);
export const IconChecking = starfishLibraryIcon('Semantics', 'icon-check');
export const IconCircleInfoLinear = starfishLibraryIcon('Semantics', 'icon-circle-info');
export const IconCircleInfoSolid = starfishLibraryIcon('Semantics', 'icon-circle-solid-info', 1.2);
export const IconCircleInformation = starfishLibraryIcon('Semantics', 'icon-circle-info');
export const IconError = starfishLibraryIcon('Semantics', 'icon-circle-solid-cross');
export const IconCross = starfishLibraryIcon('Semantics', 'icon-cross', 1.34);
export const IconCircleFill = starfishLibraryIcon('UI Essentials', 'icon-bullet', 4.26);

export const IconAllInclusiveMeal = starfishLibraryIcon('Food and beverages', 'icon-all-inclusive');
export const IconCoffee = starfishLibraryIcon('Food and beverages', 'icon-breakfast', 0.93);
export const IconFullBoardMeal = starfishLibraryIcon('Food and beverages', 'icon-full-board');
export const IconHalfBoardMeal = starfishLibraryIcon('Food and beverages', 'icon-half-board');
export const IconBed = starfishLibraryIcon('Services', 'icon-double-bed');

export const IconBaggagePersonal = starfishLibraryIcon('Luggage', 'icon-solid-backpack', 0.95);
export const IconBaggageCarryon = starfishLibraryIcon('Luggage', 'icon-solid-carry-on');
export const IconBaggageChecked = starfishLibraryIcon('Luggage', 'icon-solid-suitcase');
export const IconNoBaggagePersonal = starfishLibraryIcon('Luggage', 'icon-slashed-backpack');
export const IconNoBaggageCarryon = starfishLibraryIcon('Luggage', 'icon-slashed-carry-on');
export const IconNoBaggageChecked = starfishLibraryIcon('Luggage', 'icon-slashed-suitcase');
export const IconChangeAirport = starfishLibraryIcon('People and users', 'icon-luggage-user');

export const IconPlaneNe = starfishLibraryIcon('Transportation', 'icon-airplane-up-right', 1.04);
export const IconBus = starfishLibraryIcon('Transportation', 'icon-bus', 0.92);
export const IconBusline = starfishLibraryIcon('Transportation', 'icon-bus', 0.86);
export const IconAuto = starfishLibraryIcon('Transportation', 'icon-car', 0.93);
export const IconTicket = starfishLibraryIcon('Services', 'icon-tickets', 0.93);

export const IconSun = starfishLibraryIcon('Weather and sky', 'icon-sun', 0.92);
export const IconHalfSun = starfishLibraryIcon('Services', 'icon-sunrise');
export const IconMoon = starfishLibraryIcon('Weather and sky', 'icon-moon', 1.08);
export const IconTimer = starfishLibraryIcon('UI Essentials', 'icon-stopwatch');

export const IconUser = starfishLibraryIcon('People and users', 'icon-solid-user');
export const IconUsers = starfishLibraryIcon('People and users', 'icon-group', 0.75);

export const IconSearch = starfishLibraryIcon('UI Essentials', 'icon-magnifying-glass');
export const IconShare = starfishLibraryIcon('UI Essentials', 'icon-share', 0.83);
export const IconShareCommon = starfishLibraryIcon('Arrows', 'icon-upload', 1.25);
export const IconDetails = starfishLibraryIcon('UI Essentials', 'icon-copy');
export const IconCamera = starfishLibraryIcon('UI Essentials', 'icon-camera');
export const IconMarker = starfishLibraryIcon('Location', 'icon-pin-point');

export const IconCoins = starfishLibraryIcon('Financial', 'icon-single-coin');
export const IconTravelFlexibly = starfishLibraryIcon('Security and safety', 'icon-shield');
export const IconMobileCheck = starfishLibraryIcon('Brands and products', 'icon-app-price');

export type StarfishLibraryIcon = ReturnType<typeof starfishLibraryIcon>;
